/**
 * A utility class for working with rectangular regions.
 * Provides getters/setters for all edges and intersection checks.
 */
export class Rect {
  private x: number;
  private y: number;
  private _width: number;
  private _height: number;

  constructor({
    x,
    y,
    width,
    height,
  }: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) {
    this.x = x;
    this.y = y;
    this._width = width;
    this._height = height;
  }

  get left() {
    return this.x;
  }

  set left(value: number) {
    this.x = value;
  }

  get right() {
    return this.x + this._width;
  }

  set right(value: number) {
    this.x = value - this._width;
  }

  get top() {
    return this.y;
  }

  set top(value: number) {
    this.y = value;
  }

  get bottom() {
    return this.y + this._height;
  }

  set bottom(value: number) {
    this.y = value - this._height;
  }

  get width() {
    return this._width;
  }

  set width(value: number) {
    this._width = value;
  }

  /**
   * Sets the width while keeping the right edge fixed.
   * Unlike setting `width` directly (which preserves the left edge),
   * this method adjusts the left edge so the right edge stays in place.
   *
   * Use case: When resizing an item from its left handle, you want the
   * right edge to remain anchored while the left edge moves.
   *
   * @param value - The new width value
   */
  setWidthWhilePreservingRight(value: number) {
    this.x = this.right - value;
    this._width = value;
  }

  get height() {
    return this._height;
  }

  set height(value: number) {
    this._height = value;
  }

  isIntersectingX(other: Rect) {
    return this.left <= other.right && this.right >= other.left;
  }

  isIntersectingY(other: Rect) {
    return this.top <= other.bottom && this.bottom >= other.top;
  }

  isIntersecting(other: Rect) {
    return this.isIntersectingX(other) && this.isIntersectingY(other);
  }
}
